import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { HeroEditor, AboutEditor, CollaboratorsEditor } from "./AdminSite";
import { Plus, Trash2, Save, Edit3, X, Sparkles, Loader2, Eye, EyeOff, Image as ImageIcon, Play } from "lucide-react";

const LANGS = ["en", "ru", "es"];
const ICONS = ["Sparkles", "Tv", "Layers", "Clapperboard", "Volume2", "Pencil", "Play", "Film", "LayoutGrid"];

const COPY = {
  en: {
    subtabs: { portfolio: "Portfolio", services: "Services", featured: "Featured", hero: "Hero", about: "About", collab: "Team" },
    add: "Add",
    edit: "Edit",
    delete: "Delete",
    save: "Save",
    cancel: "Cancel",
    published: "Published",
    draft: "Draft",
    publish: "Publish",
    unpublish: "Move to draft",
    empty: "Nothing here yet — add your first entry.",
    translate: "Auto-translate",
    write_first: "Write text first in any language (EN, RU or ES)",
    translating: "Translating…",
    confirm_delete: "Delete this item? This cannot be undone.",
    fields: {
      title: "Title",
      client: "Client / subtitle",
      description: "Description",
      category: "Category",
      tags: "Tags (comma separated)",
      thumbnail: "Thumbnail URL",
      video_url: "Video URL",
      video_url_2: "Video URL 2 (outro)",
      video_duration: "Video 1 duration (e.g. 0:15)",
      video_duration_2: "Video 2 duration (e.g. 0:16)",
      gallery: "Gallery pieces — one per line: Name EN | Name RU | Name ES | image URL (order = carousel order)",
      image: "Image URL",
      youtube_id: "YouTube video ID",
      accent: "Accent color",
      price: "Starting price (USD)",
      icon: "Icon",
      year: "Year",
      duration: "Duration",
      tools: "Tools (comma separated)",
      order: "Sort order",
      client_meta: "Client name",
      status: "Status",
    },
  },
  ru: {
    subtabs: { portfolio: "Портфолио", services: "Услуги", featured: "Избранное", hero: "Главный экран", about: "О студии", collab: "Команда" },
    add: "Добавить", edit: "Редактировать", delete: "Удалить", save: "Сохранить", cancel: "Отмена",
    published: "Опубликовано", draft: "Черновик", publish: "Опубликовать", unpublish: "В черновик",
    empty: "Пока пусто — добавьте первую запись.",
    translate: "Автоперевод", write_first: "Сначала напишите текст на любом языке (EN, RU или ES)", translating: "Перевод…",
    confirm_delete: "Удалить эту запись? Нельзя отменить.",
    fields: {
      title: "Заголовок", client: "Клиент / подзаголовок", description: "Описание", category: "Категория",
      tags: "Теги (через запятую)", thumbnail: "URL превью", video_url: "URL видео", video_url_2: "URL видео 2 (аутро)",
      video_duration: "Длительность видео 1 (напр. 0:15)", video_duration_2: "Длительность видео 2 (напр. 0:16)",
      gallery: "Части галереи — по одной в строке: Название EN | Название RU | Название ES | ссылка (порядок строк = порядок карусели)",
      image: "URL изображения", youtube_id: "ID видео YouTube", accent: "Акцентный цвет",
      price: "Цена от (USD)", icon: "Иконка", year: "Год", duration: "Длительность",
      tools: "Инструменты (через запятую)", order: "Порядок", client_meta: "Имя клиента", status: "Статус",
    },
  },
  es: {
    subtabs: { portfolio: "Portafolio", services: "Servicios", featured: "Destacados", hero: "Inicio", about: "Sobre mí", collab: "Equipo" },
    add: "Añadir", edit: "Editar", delete: "Eliminar", save: "Guardar", cancel: "Cancelar",
    published: "Publicado", draft: "Borrador", publish: "Publicar", unpublish: "A borrador",
    empty: "Aún nada — agrega la primera entrada.",
    translate: "Auto-traducir", write_first: "Primero escribe texto en algún idioma (EN, RU o ES)", translating: "Traduciendo…",
    confirm_delete: "¿Eliminar este ítem? No se puede deshacer.",
    fields: {
      title: "Título", client: "Cliente / subtítulo", description: "Descripción", category: "Categoría",
      tags: "Etiquetas (separadas por coma)", thumbnail: "URL miniatura", video_url: "URL video", video_url_2: "URL video 2 (outro)",
      video_duration: "Duración video 1 (ej. 0:15)", video_duration_2: "Duración video 2 (ej. 0:16)",
      gallery: "Piezas de galería — una por línea: Nombre EN | Nombre RU | Nombre ES | URL (el orden es el del carrusel)",
      image: "URL imagen", youtube_id: "ID video de YouTube", accent: "Color de acento",
      price: "Precio desde (USD)", icon: "Icono", year: "Año", duration: "Duración",
      tools: "Herramientas (separadas por coma)", order: "Orden", client_meta: "Nombre del cliente", status: "Estado",
    },
  },
};

export default function ContentManager() {
  const { lang } = useLang();
  const C = COPY[lang] || COPY.en;
  const [sub, setSub] = useState("portfolio");

  return (
    <div>
      <div className="flex flex-wrap gap-px bg-white/20 border border-white/20 w-fit mb-6">
        {Object.entries(C.subtabs).map(([k, label]) => (
          <button
            key={k}
            data-testid={`content-sub-${k}`}
            onClick={() => setSub(k)}
            className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
              sub === k ? "bg-[#00d4ff] text-[#0a0a0a]" : "bg-[#0a0a0a] text-white hover:bg-white/10"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {sub === "portfolio" && <PortfolioManager C={C} />}
      {sub === "services" && <ServicesManager C={C} />}
      {sub === "featured" && <FeaturedManager C={C} />}
      {sub === "hero" && <HeroEditor />}
      {sub === "about" && <AboutEditor />}
      {sub === "collab" && <CollaboratorsEditor />}
    </div>
  );
}

/* ============================================================ */
/*                        PORTFOLIO                             */
/* ============================================================ */
function PortfolioManager({ C }) {
  // el editor de proyectos ahora es una pagina aparte, con vista previa de la ficha
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/portfolio");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const newItem = () => setEditing({
    title: { en: "", ru: "", es: "" },
    category: { en: "", ru: "", es: "" },
    description: { en: "", ru: "", es: "" },
    tags: [], thumbnail: "", video_url: "",
    client: "", year: new Date().getFullYear(), duration: "",
    tools: [], color_a: "#00D4FF", color_b: "#FF006E",
    status: "published", order: items.length,
    _new: true,
  });

  const remove = async (id) => {
    if (!window.confirm(C.confirm_delete)) return;
    await api.delete(`/admin/portfolio/${id}`);
    load();
  };

  // arrastrar una tarjeta para cambiar el orden: se ve al instante y se guarda solo
  const reordenar = async (desde, hasta) => {
    if (desde === null || hasta === null || desde === hasta) return;
    const lista = [...items];
    const [movida] = lista.splice(desde, 1);
    lista.splice(hasta, 0, movida);
    const renumerada = lista.map((x, i) => ({ ...x, order: i }));
    setItems(renumerada);
    for (const x of renumerada) {
      const cuerpo = { ...x };
      delete cuerpo.project_id;
      delete cuerpo.created_at;
      delete cuerpo.updated_at;
      try {
        await api.put(`/admin/portfolio/${x.project_id}`, cuerpo);
      } catch (e) {
        // si uno falla, sigo con el resto para no dejar el orden a medias
      }
    }
    load();
  };

  return (
    <ItemGrid
      C={C}
      items={items}
      onAdd={() => navigate("/dashboard/portfolio/new")}
      onEdit={(x) => navigate(`/dashboard/portfolio/${x.project_id}`)}
      onDelete={(x) => remove(x.project_id)}
      onReorder={reordenar}
      renderThumb={(x) => (
        x.thumbnail ? <img src={x.thumbnail} alt="" className="w-full h-full object-cover" /> :
        <div className="w-full h-full" style={{ background: `linear-gradient(135deg, ${x.color_a || "#00d4ff"}, ${x.color_b || "#ff006e"})` }} />
      )}
      getTitle={(x) => (typeof x.title === "object" ? x.title.en : x.title) || "(untitled)"}
      getSub={(x) => (typeof x.category === "object" ? x.category.en : x.category) || ""}
      testidPrefix="portfolio"
    >
      {editing && (
        <PortfolioEditor
          C={C}
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </ItemGrid>
  );
}

function PortfolioEditor({ C, item, onClose, onSaved }) {
  const [form, setForm] = useState({
    ...item,
    title: typeof item.title === "object" ? item.title : { en: item.title || "", ru: "", es: "" },
    category: typeof item.category === "object" ? item.category : { en: item.category || "", ru: "", es: "" },
    description: typeof item.description === "object" ? item.description : { en: item.description || "", ru: "", es: "" },
    tags: Array.isArray(item.tags) ? item.tags : [],
    tools: Array.isArray(item.tools) ? item.tools : [],
    video_url: item.video_url || "",
    video_url_2: item.video_url_2 || "",
    video_duration: item.video_duration || "",
    video_duration_2: item.video_duration_2 || "",
    gallery: (Array.isArray(item.process_images) ? item.process_images : [])
      .map((u, i) => {
        const t = (item.process_titles || [])[i];
        const nom = t && typeof t === "object" ? [t.en, t.ru, t.es] : [t || "", "", ""];
        const hayNombre = nom.some((x) => x && String(x).trim());
        return (hayNombre ? `${nom.map((x) => x || "").join(" | ")} | ` : "") + u;
      })
      .join("\n"),
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // un solo idioma para todo el formulario: el selector va arriba, no repetido por campo
  const [lang, setLang] = useState("ru");

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = { ...form };

      // "Nombre EN | Nombre RU | Nombre ES | URL" por línea -> dos listas en el mismo orden
      const lineas = String(form.gallery || "").split("\n").map((s) => s.trim()).filter(Boolean);
      const imgs = [];
      const titulos = [];
      lineas.forEach((ln) => {
        const partes = ln.split("|").map((s) => s.trim());
        const url = partes.pop() || "";
        if (!url) return;
        imgs.push(url);
        titulos.push({ en: partes[0] || "", ru: partes[1] || "", es: partes[2] || "" });
      });
      payload.process_images = imgs;
      payload.process_titles = titulos;
      delete payload.gallery;

      delete payload._new;
      delete payload.project_id;
      delete payload.created_at;
      delete payload.updated_at;
      if (item._new) await api.post("/admin/portfolio", payload);
      else await api.put(`/admin/portfolio/${item.project_id}`, payload);
      onSaved();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="Portfolio Item">
      {/* Selector unico de idioma: cambia de una vez todos los campos de texto
          (titulo, categoria, descripcion). Las URLs y los datos tecnicos son
          los mismos en los 3 idiomas, asi que no se tocan al cambiar. */}
      <div className="mb-5 flex items-center justify-between gap-4 border border-white/15 px-4 py-3 bg-white/[0.02]">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
          idioma del contenido
        </span>
        <div className="flex items-center gap-1">
          {["ru", "en", "es"].map((L) => (
            <button
              key={L}
              type="button"
              onClick={() => setLang(L)}
              data-testid={`form-lang-${L}`}
              className={`px-4 py-1.5 border font-mono text-[11px] uppercase tracking-[0.2em] transition-colors ${
                lang === L
                  ? "border-white/70 text-white bg-white/15"
                  : "border-white/20 text-white/50 hover:text-white/90"
              }`}
            >
              {L}
            </button>
          ))}
        </div>
      </div>
      <I18nField C={C} name="title" lang={lang} label={C.fields.title} value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
      <I18nField C={C} name="category" lang={lang} label={C.fields.category} value={form.category} onChange={(v) => setForm({ ...form, category: v })} />
      <I18nField C={C} name="description" lang={lang} label={C.fields.description} value={form.description} onChange={(v) => setForm({ ...form, description: v })} multiline />

      <div className="grid grid-cols-2 gap-4">
        <TextField label={C.fields.thumbnail} value={form.thumbnail} onChange={(v) => setForm({ ...form, thumbnail: v })} />
        <TextField label={C.fields.video_url} value={form.video_url} onChange={(v) => setForm({ ...form, video_url: v })} />
        <TextField label={C.fields.video_url_2} value={form.video_url_2} onChange={(v) => setForm({ ...form, video_url_2: v })} />
        <TextField label={C.fields.video_duration} value={form.video_duration} onChange={(v) => setForm({ ...form, video_duration: v })} />
        <TextField label={C.fields.video_duration_2} value={form.video_duration_2} onChange={(v) => setForm({ ...form, video_duration_2: v })} />
        <TextField label={C.fields.client_meta} value={form.client} onChange={(v) => setForm({ ...form, client: v })} />
        <TextField label={C.fields.year} type="number" value={form.year} onChange={(v) => setForm({ ...form, year: parseInt(v) || 0 })} />
        <TextField label={C.fields.duration} value={form.duration} onChange={(v) => setForm({ ...form, duration: v })} />
        <TextField label={C.fields.order} type="number" value={form.order} onChange={(v) => setForm({ ...form, order: parseInt(v) || 0 })} />
        <TextField label={C.fields.tags} value={form.tags.join(", ")} onChange={(v) => setForm({ ...form, tags: v.split(",").map((x) => x.trim()).filter(Boolean) })} />
        <TextField label={C.fields.tools} value={form.tools.join(", ")} onChange={(v) => setForm({ ...form, tools: v.split(",").map((x) => x.trim()).filter(Boolean) })} />
      </div>

      <TextArea
        label={C.fields.gallery}
        value={form.gallery}
        onChange={(v) => setForm({ ...form, gallery: v })}
        placeholder={"Storyboard | https://...\nAvatar | https://..."}
      />

      <StatusToggle C={C} status={form.status} onChange={(s) => setForm({ ...form, status: s })} />

      <ModalFooter C={C} err={err} busy={busy} onCancel={onClose} onSave={save} />
    </Modal>
  );
}

/* ============================================================ */
/*                        SERVICES                              */
/* ============================================================ */
function ServicesManager({ C }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/services");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const newItem = () => setEditing({
    title: { en: "", ru: "", es: "" },
    description: { en: "", ru: "", es: "" },
    price: 0, icon: "Sparkles",
    order: items.length, status: "published",
    _new: true,
  });

  const remove = async (id) => {
    if (!window.confirm(C.confirm_delete)) return;
    await api.delete(`/admin/services/${id}`);
    load();
  };

  return (
    <ItemGrid
      C={C}
      items={items}
      onAdd={newItem}
      onEdit={setEditing}
      onDelete={(x) => remove(x.service_id)}
      renderThumb={() => (
        <div className="w-full h-full flex items-center justify-center bg-[#0a0a0a]">
          <Sparkles size={28} className="text-[#00d4ff]" />
        </div>
      )}
      getTitle={(x) => x.title?.en || "(untitled)"}
      getSub={(x) => `$${x.price}+`}
      testidPrefix="service"
    >
      {editing && (
        <ServiceEditor
          C={C}
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </ItemGrid>
  );
}

function ServiceEditor({ C, item, onClose, onSaved }) {
  const [form, setForm] = useState(item);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = { ...form };
      delete payload._new; delete payload.service_id;
      delete payload.created_at; delete payload.updated_at;
      if (item._new) await api.post("/admin/services", payload);
      else await api.put(`/admin/services/${item.service_id}`, payload);
      onSaved();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="Service">
      <I18nField C={C} name="title" label={C.fields.title} value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
      <I18nField C={C} name="description" label={C.fields.description} value={form.description} onChange={(v) => setForm({ ...form, description: v })} multiline />
      <div className="grid grid-cols-3 gap-4">
        <TextField label={C.fields.price} type="number" value={form.price} onChange={(v) => setForm({ ...form, price: parseFloat(v) || 0 })} />
        <SelectField label={C.fields.icon} value={form.icon} onChange={(v) => setForm({ ...form, icon: v })} options={ICONS} />
        <TextField label={C.fields.order} type="number" value={form.order} onChange={(v) => setForm({ ...form, order: parseInt(v) || 0 })} />
      </div>
      <StatusToggle C={C} status={form.status} onChange={(s) => setForm({ ...form, status: s })} />
      <ModalFooter C={C} err={err} busy={busy} onCancel={onClose} onSave={save} />
    </Modal>
  );
}

/* ============================================================ */
/*                        FEATURED                              */
/* ============================================================ */
function FeaturedManager({ C }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/featured");
    setItems(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const newItem = () => setEditing({
    title: "",
    client: { en: "", ru: "", es: "" },
    description: { en: "", ru: "", es: "" },
    image: "", youtube_id: "", accent: "#00d4ff",
    order: items.length, status: "published",
    _new: true,
  });

  const remove = async (id) => {
    if (!window.confirm(C.confirm_delete)) return;
    await api.delete(`/admin/featured/${id}`);
    load();
  };

  return (
    <ItemGrid
      C={C}
      items={items}
      onAdd={newItem}
      onEdit={setEditing}
      onDelete={(x) => remove(x.featured_id)}
      renderThumb={(x) => (
        x.image ? <img src={x.image} alt="" className="w-full h-full object-cover" /> :
        <div className="w-full h-full flex items-center justify-center bg-[#0a0a0a]"><Play size={28} className="text-white" /></div>
      )}
      getTitle={(x) => x.title || "(untitled)"}
      getSub={(x) => x.client?.en || ""}
      testidPrefix="featured"
    >
      {editing && (
        <FeaturedEditor
          C={C}
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </ItemGrid>
  );
}

function FeaturedEditor({ C, item, onClose, onSaved }) {
  const [form, setForm] = useState(item);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = { ...form };
      delete payload._new; delete payload.featured_id;
      delete payload.created_at; delete payload.updated_at;
      if (item._new) await api.post("/admin/featured", payload);
      else await api.put(`/admin/featured/${item.featured_id}`, payload);
      onSaved();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="Featured Item">
      <TextField label={C.fields.title} value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
      <I18nField C={C} name="client" label={C.fields.client} value={form.client} onChange={(v) => setForm({ ...form, client: v })} />
      <I18nField C={C} name="description" label={C.fields.description} value={form.description} onChange={(v) => setForm({ ...form, description: v })} multiline />
      <div className="grid grid-cols-2 gap-4">
        <TextField label={C.fields.image} value={form.image} onChange={(v) => setForm({ ...form, image: v })} />
        <TextField label={C.fields.youtube_id} value={form.youtube_id} onChange={(v) => setForm({ ...form, youtube_id: v })} placeholder="dQw4w9WgXcQ" />
        <TextField label={C.fields.accent} value={form.accent} onChange={(v) => setForm({ ...form, accent: v })} placeholder="#ff006e" />
        <TextField label={C.fields.order} type="number" value={form.order} onChange={(v) => setForm({ ...form, order: parseInt(v) || 0 })} />
      </div>
      <StatusToggle C={C} status={form.status} onChange={(s) => setForm({ ...form, status: s })} />
      <ModalFooter C={C} err={err} busy={busy} onCancel={onClose} onSave={save} />
    </Modal>
  );
}

/* ============================================================ */
/*                        SHARED WIDGETS                        */
/* ============================================================ */
function ItemGrid({ C, items, onAdd, onEdit, onDelete, renderThumb, getTitle, getSub, testidPrefix, onReorder, children }) {
  // si viene onReorder, las tarjetas se pueden arrastrar para cambiar el orden
  const [arrastra, setArrastra] = useState(null);
  const [sobre, setSobre] = useState(null);

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
          {items.length} items
        </div>
        <button
          onClick={onAdd}
          data-testid={`${testidPrefix}-add-btn`}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#00d4ff] text-[#0a0a0a] font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-white transition-colors"
        >
          <Plus size={12} /> {C.add}
        </button>
      </div>

      {items.length === 0 ? (
        <div className="border border-white/20 bg-white/[0.02] p-10 font-mono text-xs text-white/40">
          {C.empty}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((x, i) => (
            <div
              key={x.project_id || x.service_id || x.featured_id || i}
              data-testid={`${testidPrefix}-card-${i}`}
              draggable={!!onReorder}
              onDragStart={() => onReorder && setArrastra(i)}
              onDragOver={(e) => { if (onReorder) { e.preventDefault(); setSobre(i); } }}
              onDrop={(e) => { if (onReorder) { e.preventDefault(); onReorder(arrastra, i); setArrastra(null); setSobre(null); } }}
              onDragEnd={() => { setArrastra(null); setSobre(null); }}
              title={onReorder ? "arrastrá para cambiar el orden" : undefined}
              className={`border bg-white/[0.02] overflow-hidden group ${
                sobre === i && arrastra !== null ? "border-[#00d4ff]" : "border-white/20"
              } ${arrastra === i ? "opacity-40" : ""} ${onReorder ? "cursor-move" : ""}`}
            >
              <div className="relative aspect-video overflow-hidden bg-[#0a0a0a]">
                {renderThumb(x)}
                <span className={`absolute top-2 left-2 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.2em] ${
                  x.status === "draft" ? "bg-white/20 text-white" : "bg-[#00d4ff] text-[#0a0a0a]"
                }`}>
                  {x.status === "draft" ? C.draft : C.published}
                </span>
                {onReorder && (
                  <span className="absolute top-2 right-2 px-2 py-1 bg-[#0a0a0a]/80 font-mono text-[9px] text-white">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                )}
              </div>
              <div className="p-4">
                <div className="font-mono text-sm text-white truncate">{getTitle(x)}</div>
                <div className="mt-1 font-mono text-[10px] text-white/40 truncate">{getSub(x)}</div>
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => onEdit(x)}
                    data-testid={`${testidPrefix}-edit-${i}`}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 border border-white/30 hover:border-[#00d4ff] font-mono text-[10px] uppercase tracking-[0.2em]"
                  >
                    <Edit3 size={11} /> {C.edit}
                  </button>
                  <button
                    onClick={() => onDelete(x)}
                    data-testid={`${testidPrefix}-delete-${i}`}
                    className="px-3 py-2 border border-white/30 hover:border-[#ff006e] hover:text-[#ff006e] font-mono text-[10px]"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {children}
    </div>
  );
}

function Modal({ children, onClose, title }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-[#0a0a0a]/90 backdrop-blur-md overflow-y-auto"
        onClick={onClose}
        data-testid="content-editor-modal"
      >
        <motion.div
          initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 10, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="max-w-3xl mx-auto my-8 bg-[#111] border border-white/20 text-white"
        >
          <div className="px-6 py-4 border-b border-white/20 flex items-center justify-between">
            <h3 className="font-mono text-sm uppercase tracking-[0.25em]">{title}</h3>
            <button onClick={onClose} data-testid="content-editor-close" className="p-1 hover:text-[#ff006e]"><X size={18} /></button>
          </div>
          <div className="p-6 space-y-5">{children}</div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function TextField({ label, value, onChange, type = "text", placeholder }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1.5">{label}</div>
      <input
        type={type}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-transparent border border-white/30 text-white font-sans text-sm focus:outline-none focus:border-[#00d4ff]"
      />
    </label>
  );
}

function TextArea({ label, value, onChange, rows = 7, placeholder }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1.5">{label}</div>
      <textarea
        rows={rows}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-transparent border border-white/30 text-white font-mono text-xs leading-relaxed focus:outline-none focus:border-[#00d4ff]"
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1.5">{label}</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-[#0a0a0a] border border-white/30 text-white font-sans text-sm focus:outline-none focus:border-[#00d4ff]"
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function I18nField({ C, name, label, value, onChange, multiline, lang }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const v = value || { en: "", ru: "", es: "" };
  const key = name || String(label || "field").toLowerCase().replace(/[^a-z0-9]+/g, "-");
  // El idioma puede venir del formulario (un selector para todos los campos) o
  // elegirse dentro del propio campo. El ruso va primero: es el de trabajo.
  const [tabLocal, setTabLocal] = useState("ru");
  const tab = lang || tabLocal;
  const setTab = setTabLocal;
  const conPestanas = !lang;   // si el formulario ya manda el idioma, no repite las pestanas

  const translate = async () => {
    // Detect the language the user actually wrote (any of the three)
    // la fuente es la pestana activa; si esta vacia, el primer idioma con texto
    const src = (v[tab] || "").trim() ? tab : LANGS.find((L) => (v[L] || "").trim());
    if (!src) { setErr(C.write_first); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/translate", { text: v[src].trim(), from_lang: src });
      onChange({ en: data.en || v.en || "", ru: data.ru || v.ru || "", es: data.es || v.es || "" });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-white/10 p-4 bg-white/[0.02]">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">{label}</div>
        <button
          type="button"
          onClick={translate}
          disabled={busy}
          data-testid={`translate-${key}`}
          className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#00d4ff] text-[#00d4ff] hover:bg-[#00d4ff] hover:text-[#0a0a0a] font-mono text-[10px] uppercase tracking-[0.18em] disabled:opacity-50"
        >
          {busy ? <><Loader2 size={11} className="animate-spin" /> {C.translating}</> : <><Sparkles size={11} /> {C.translate}</>}
        </button>
      </div>
      {/* pestanas de idioma + un solo campo visible (asi el modal queda corto).
          El punto azul marca los idiomas que ya tienen texto. */}
      <div className={`flex items-center gap-1 ${conPestanas ? "" : "hidden"}`}>
        {["ru", "en", "es"].map((L) => (
          <button
            key={L}
            type="button"
            onClick={() => setTab(L)}
            data-testid={`langtab-${L}-${key}`}
            className={`px-3 py-1 border font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
              tab === L
                ? "border-white/60 text-white bg-white/10"
                : "border-white/15 text-white/50 hover:text-white/90"
            }`}
          >
            {L}
            {(v[L] || "").trim() ? <span className="ml-1 text-[#00d4ff]">•</span> : null}
          </button>
        ))}
      </div>
      <div className="mt-2">
        {multiline ? (
          <textarea
            data-testid={`i18n-${tab}-${key}`}
            value={v[tab] || ""}
            rows={4}
            onChange={(e) => onChange({ ...v, [tab]: e.target.value })}
            className="w-full px-3 py-2 bg-transparent border border-white/20 text-white font-sans text-sm focus:outline-none focus:border-white/60 resize-y"
          />
        ) : (
          <input
            data-testid={`i18n-${tab}-${key}`}
            value={v[tab] || ""}
            onChange={(e) => onChange({ ...v, [tab]: e.target.value })}
            className="w-full px-3 py-2 bg-transparent border border-white/20 text-white font-sans text-sm focus:outline-none focus:border-white/60"
          />
        )}
      </div>
      {err && <div className="mt-2 font-mono text-[10px] text-[#ff006e]">{err}</div>}
    </div>
  );
}

function StatusToggle({ C, status, onChange }) {
  return (
    <div className="flex items-center gap-3">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">{C.fields.status}</span>
      <button
        onClick={() => onChange(status === "published" ? "draft" : "published")}
        data-testid="content-status-toggle"
        className={`inline-flex items-center gap-2 px-3 py-1.5 border font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
          status === "published"
            ? "border-[#00d4ff] text-[#00d4ff]"
            : "border-white/40 text-white/70"
        }`}
      >
        {status === "published" ? <><Eye size={12} /> {C.published}</> : <><EyeOff size={12} /> {C.draft}</>}
      </button>
    </div>
  );
}

function ModalFooter({ C, err, busy, onCancel, onSave }) {
  return (
    <>
      {err && <div className="font-mono text-xs text-[#ff006e]">{err}</div>}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
        <button onClick={onCancel} className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 hover:text-white px-3 py-2">
          {C.cancel}
        </button>
        <button
          onClick={onSave}
          disabled={busy}
          data-testid="content-editor-save"
          className="inline-flex items-center gap-2 px-5 py-2 bg-[#00d4ff] text-[#0a0a0a] font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-white transition-colors disabled:opacity-50"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          {C.save}
        </button>
      </div>
    </>
  );
}
